import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PortalShell, { StatusPill } from "../components/PortalShell";
import NewVerificationDialog from "../components/NewVerificationDialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../components/ui/tabs";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { api, fmtErr, inr, fileUrl } from "../api";
import { toast } from "sonner";
import { Building2, Layers, Users, ShieldCheck, Plus, ArrowRight, FileText, Inbox, ImagePlus, X } from "lucide-react";

export default function BuilderDashboard() {
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");
  const [overview, setOverview] = useState(null);
  const [projects, setProjects] = useState([]);
  const [units, setUnits] = useState([]);
  const [buyers, setBuyers] = useState([]);
  const [applications, setApplications] = useState([]);
  const [verifications, setVerifications] = useState([]);
  const [showProject, setShowProject] = useState(false);
  const [showUnit, setShowUnit] = useState(false);
  const [showBuyer, setShowBuyer] = useState(false);
  const [showVerification, setShowVerification] = useState(false);
  const [projectForm, setProjectForm] = useState({ name: "", location: "", city: "Bengaluru", rera_id: "", status: "Ongoing", description: "" });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState("");
  const [saving, setSaving] = useState(false);
  const [unitForm, setUnitForm] = useState({ project_id: "", unit_no: "", unit_type: "2 BHK", area_sqft: 0, price: 0, floor: 0, status: "available" });
  const [buyerForm, setBuyerForm] = useState({ project_id: "", name: "", phone: "", email: "", kyc_status: "Pending", booking_status: "Enquiry", notes: "" });

  const load = useCallback(async () => {
    try {
      const [o, p, u, b, a, v] = await Promise.all([
        api.get("/builder/overview"), api.get("/builder/projects"), api.get("/builder/units"),
        api.get("/builder/buyers"), api.get("/builder/applications"), api.get("/verifications"),
      ]);
      setOverview(o.data); setProjects(p.data); setUnits(u.data);
      setBuyers(b.data); setApplications(a.data); setVerifications(v.data);
    } catch (e) { toast.error(fmtErr(e)); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const pickPhoto = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setPhotoFile(f);
    setPhotoPreview(URL.createObjectURL(f));
  };

  const resetProjectForm = () => {
    setProjectForm({ name: "", location: "", city: "Bengaluru", rera_id: "", status: "Ongoing", description: "" });
    setPhotoFile(null);
    if (photoPreview) URL.revokeObjectURL(photoPreview);
    setPhotoPreview("");
  };

  const submitProject = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data: created } = await api.post("/builder/projects", { ...projectForm, published: true });
      if (photoFile) {
        const fd = new FormData();
        fd.append("file", photoFile);
        await api.post(`/builder/projects/${created.id}/photo`, fd);
      }
      toast.success("Project published.");
      setShowProject(false);
      resetProjectForm();
      load();
    } catch (err) { toast.error(fmtErr(err)); }
    finally { setSaving(false); }
  };

  const submitUnit = async (e) => {
    e.preventDefault();
    try {
      await api.post("/builder/units", { ...unitForm, area_sqft: Number(unitForm.area_sqft), price: Number(unitForm.price), floor: Number(unitForm.floor) });
      toast.success("Unit added.");
      setShowUnit(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const submitBuyer = async (e) => {
    e.preventDefault();
    try {
      await api.post("/builder/buyers", buyerForm);
      toast.success("Buyer added.");
      setShowBuyer(false);
      load();
    } catch (err) { toast.error(fmtErr(err)); }
  };

  const updateUnitStatus = async (id, status) => {
    try { await api.patch(`/builder/units/${id}`, { status }); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const updateBuyer = async (id, patch) => {
    try { await api.patch(`/builder/buyers/${id}`, patch); load(); } catch (e) { toast.error(fmtErr(e)); }
  };
  const updateApplication = async (id, status) => {
    try { await api.patch(`/builder/applications/${id}`, { status }); toast.success(`Application ${status}.`); load(); } catch (e) { toast.error(fmtErr(e)); }
  };

  const kpis = overview ? [
    { label: "Projects", value: overview.projects, icon: Building2, tid: "kpi-projects" },
    { label: "Total Units", value: overview.units_total, icon: Layers, tid: "kpi-units" },
    { label: "Units Available", value: overview.units_available, icon: Layers, tid: "kpi-available" },
    { label: "Booked / Sold", value: overview.units_booked, icon: Layers, tid: "kpi-booked" },
    { label: "Buyers", value: overview.buyers, icon: Users, tid: "kpi-buyers" },
    { label: "New Applications", value: overview.pending_applications, icon: Inbox, tid: "kpi-applications" },
    { label: "Verifications", value: overview.verifications_total, icon: ShieldCheck, tid: "kpi-verifications" },
    { label: "Reports Completed", value: overview.verifications_completed, icon: FileText, tid: "kpi-reports" },
  ] : [];

  return (
    <PortalShell title="Builder Workspace" subtitle="Projects, units, buyers and verification pipeline"
      actions={<Button data-testid="builder-new-verification-button" onClick={() => setShowVerification(true)}><Plus className="mr-1.5 h-4 w-4" /> New Verification</Button>}>
      <NewVerificationDialog open={showVerification} onOpenChange={setShowVerification} />

      {/* Add project dialog */}
      <Dialog open={showProject} onOpenChange={(v) => { setShowProject(v); if (!v) resetProjectForm(); }}>
        <DialogContent className="max-w-3xl" data-testid="add-project-dialog">
          <DialogHeader><DialogTitle>Add New Project</DialogTitle></DialogHeader>
          <form onSubmit={submitProject} className="grid grid-cols-1 gap-6 md:grid-cols-2">
            <div className="space-y-4">
              <div className="space-y-1.5"><Label>Project name *</Label><Input required data-testid="project-name-input" value={projectForm.name} onChange={(e) => setProjectForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>Location *</Label><Input required data-testid="project-location-input" value={projectForm.location} onChange={(e) => setProjectForm((f) => ({ ...f, location: e.target.value }))} /></div>
                <div className="space-y-1.5"><Label>City</Label><Input data-testid="project-city-input" value={projectForm.city} onChange={(e) => setProjectForm((f) => ({ ...f, city: e.target.value }))} /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5"><Label>RERA ID</Label><Input data-testid="project-rera-input" value={projectForm.rera_id} onChange={(e) => setProjectForm((f) => ({ ...f, rera_id: e.target.value }))} placeholder="PRM/KA/RERA/…" /></div>
                <div className="space-y-1.5"><Label>Status</Label>
                  <Select value={projectForm.status} onValueChange={(v) => setProjectForm((f) => ({ ...f, status: v }))}>
                    <SelectTrigger data-testid="project-status-select"><SelectValue /></SelectTrigger>
                    <SelectContent>{["New Launch", "Ongoing", "Completed"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-1.5"><Label>Description</Label><Textarea rows={3} data-testid="project-description-input" value={projectForm.description} onChange={(e) => setProjectForm((f) => ({ ...f, description: e.target.value }))} /></div>
              <div className="space-y-1.5">
                <Label>Project photo</Label>
                {photoPreview ? (
                  <div className="relative" data-testid="project-photo-preview">
                    <img src={photoPreview} alt="Project" className="h-36 w-full rounded-lg border border-slate-200 object-cover" />
                    <button type="button" onClick={() => { setPhotoFile(null); URL.revokeObjectURL(photoPreview); setPhotoPreview(""); }}
                      className="absolute right-2 top-2 rounded-full bg-slate-950/70 p-1.5 text-white hover:bg-slate-950" data-testid="project-photo-remove">
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <label className="flex cursor-pointer flex-col items-center gap-2 rounded-lg border-2 border-dashed border-blue-200 bg-blue-50/50 px-4 py-6 text-center transition-colors hover:border-blue-400" data-testid="project-photo-dropzone">
                    <ImagePlus className="h-6 w-6 text-blue-600" />
                    <span className="text-xs font-medium text-slate-600">Click to upload project photo (JPG / PNG / WEBP)</span>
                    <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={pickPhoto} data-testid="project-photo-input" />
                  </label>
                )}
              </div>
            </div>
            <div className="space-y-2">
              <p className="text-[11px] font-bold uppercase tracking-widest text-slate-400">Live preview — how buyers see it</p>
              <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm" data-testid="project-preview-card">
                {photoPreview
                  ? <img src={photoPreview} alt="Preview" className="h-40 w-full object-cover" />
                  : <div className="flex h-40 items-center justify-center bg-slate-100 text-xs text-slate-400">Project photo appears here</div>}
                <div className="p-4">
                  <div className="flex items-center justify-between gap-2">
                    <p className="truncate text-sm font-semibold text-slate-900">{projectForm.name || "Project name"}</p>
                    <span className="shrink-0 rounded-full bg-blue-50 px-2 py-0.5 text-[10px] font-bold uppercase text-blue-700">{projectForm.status}</span>
                  </div>
                  <p className="mt-0.5 text-xs text-slate-500">{projectForm.location || "Location"}, {projectForm.city || "City"}</p>
                  {projectForm.rera_id && <p className="mt-0.5 truncate font-mono text-[10px] text-slate-400">RERA: {projectForm.rera_id}</p>}
                  {projectForm.description && <p className="mt-2 line-clamp-2 text-xs text-slate-600">{projectForm.description}</p>}
                </div>
              </div>
              <p className="text-[11px] leading-relaxed text-slate-400">Published projects appear on the home page slider and in the Property Directory for buyers.</p>
            </div>
            <Button type="submit" className="md:col-span-2" disabled={saving} data-testid="project-submit-button">
              {saving ? "Publishing…" : "Publish Project"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add unit dialog */}
      <Dialog open={showUnit} onOpenChange={setShowUnit}>
        <DialogContent className="max-w-lg" data-testid="add-unit-dialog">
          <DialogHeader><DialogTitle>Add Unit</DialogTitle></DialogHeader>
          <form onSubmit={submitUnit} className="space-y-4">
            <div className="space-y-1.5"><Label>Project</Label>
              <Select value={unitForm.project_id} onValueChange={(v) => setUnitForm((f) => ({ ...f, project_id: v }))}>
                <SelectTrigger data-testid="unit-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5"><Label>Unit No. *</Label><Input required data-testid="unit-no-input" value={unitForm.unit_no} onChange={(e) => setUnitForm((f) => ({ ...f, unit_no: e.target.value }))} placeholder="A-101" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={unitForm.unit_type} onValueChange={(v) => setUnitForm((f) => ({ ...f, unit_type: v }))}>
                  <SelectTrigger data-testid="unit-type-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{["1 BHK", "2 BHK", "3 BHK", "4 BHK", "Plot", "Commercial"].map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5"><Label>Area (sq.ft)</Label><Input type="number" data-testid="unit-area-input" value={unitForm.area_sqft} onChange={(e) => setUnitForm((f) => ({ ...f, area_sqft: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Price (₹)</Label><Input type="number" data-testid="unit-price-input" value={unitForm.price} onChange={(e) => setUnitForm((f) => ({ ...f, price: e.target.value }))} /></div>
            </div>
            <Button type="submit" className="w-full" disabled={!unitForm.project_id} data-testid="unit-submit-button">Add Unit</Button>
          </form>
        </DialogContent>
      </Dialog>

      {/* Add buyer dialog */}
      <Dialog open={showBuyer} onOpenChange={setShowBuyer}>
        <DialogContent className="max-w-lg" data-testid="add-buyer-dialog">
          <DialogHeader><DialogTitle>Add Buyer</DialogTitle></DialogHeader>
          <form onSubmit={submitBuyer} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="col-span-2 space-y-1.5"><Label>Buyer name *</Label><Input required data-testid="buyer-name-input" value={buyerForm.name} onChange={(e) => setBuyerForm((f) => ({ ...f, name: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Phone</Label><Input data-testid="buyer-phone-input" value={buyerForm.phone} onChange={(e) => setBuyerForm((f) => ({ ...f, phone: e.target.value }))} /></div>
              <div className="space-y-1.5"><Label>Email</Label><Input type="email" data-testid="buyer-email-input" value={buyerForm.email} onChange={(e) => setBuyerForm((f) => ({ ...f, email: e.target.value }))} /></div>
              <div className="col-span-2 space-y-1.5"><Label>Project</Label>
                <Select value={buyerForm.project_id} onValueChange={(v) => setBuyerForm((f) => ({ ...f, project_id: v }))}>
                  <SelectTrigger data-testid="buyer-project-select"><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{projects.map((p) => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" className="w-full" data-testid="buyer-submit-button">Add Buyer</Button>
          </form>
        </DialogContent>
      </Dialog>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList data-testid="builder-tabs" className="w-full justify-start overflow-x-auto">
          <TabsTrigger value="overview" data-testid="tab-builder-overview">Overview</TabsTrigger>
          <TabsTrigger value="projects" data-testid="tab-builder-projects">Projects</TabsTrigger>
          <TabsTrigger value="units" data-testid="tab-builder-units">Units</TabsTrigger>
          <TabsTrigger value="buyers" data-testid="tab-builder-buyers">Buyers</TabsTrigger>
          <TabsTrigger value="applications" data-testid="tab-builder-applications">Applications</TabsTrigger>
          <TabsTrigger value="verifications" data-testid="tab-builder-verifications">Verifications</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="mt-6" data-testid="builder-overview-panel">
          <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
            {kpis.map((k) => (
              <div key={k.label} className="card-lift rounded-xl border border-slate-200 bg-white p-5" data-testid={k.tid}>
                <k.icon className="h-5 w-5 text-blue-600" />
                <p className="mt-3 text-3xl font-extrabold text-slate-900">{k.value}</p>
                <p className="mt-1 text-xs font-medium text-slate-500">{k.label}</p>
              </div>
            ))}
          </div>
        </TabsContent>

        <TabsContent value="projects" className="mt-6" data-testid="builder-projects-panel">
          <div className="mb-4"><Button data-testid="add-project-button" onClick={() => setShowProject(true)}><Plus className="mr-1.5 h-4 w-4" /> Add Project</Button></div>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3">
            {projects.map((p) => (
              <div key={p.id} className="card-lift overflow-hidden rounded-xl border border-slate-200 bg-white" data-testid={`builder-project-${p.id}`}>
                {p.image_url && <img src={fileUrl(p.image_url)} alt={p.name} className="h-32 w-full object-cover" />}
                <div className="p-5">
                <div className="flex items-center justify-between gap-2">
                  <p className="font-semibold text-slate-900">{p.name}</p>
                  <span className="rounded-full bg-blue-50 px-2.5 py-1 text-[10px] font-bold uppercase text-blue-700">{p.status}</span>
                </div>
                <p className="mt-1 text-xs text-slate-500">{p.location}, {p.city}</p>
                {p.rera_id && <p className="mt-1 font-mono text-[10px] text-slate-400">RERA: {p.rera_id}</p>}
                <div className="mt-4 flex gap-4 text-sm">
                  <span><b>{p.units_total}</b> units</span>
                  <span className="text-emerald-600"><b>{p.units_available}</b> available</span>
                </div>
                </div>
              </div>
            ))}
            {projects.length === 0 && <p className="text-sm text-slate-500">No projects yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="units" className="mt-6" data-testid="builder-units-panel">
          <div className="mb-4"><Button data-testid="add-unit-button" onClick={() => setShowUnit(true)}><Plus className="mr-1.5 h-4 w-4" /> Add Unit</Button></div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-5 py-3">Unit</th><th className="px-5 py-3">Type</th><th className="px-5 py-3">Area</th><th className="px-5 py-3">Price</th><th className="px-5 py-3">Status</th></tr>
              </thead>
              <tbody>
                {units.map((u) => (
                  <tr key={u.id} className="border-t border-slate-100" data-testid={`unit-row-${u.id}`}>
                    <td className="px-5 py-3 font-semibold">{u.unit_no}</td>
                    <td className="px-5 py-3">{u.unit_type}</td>
                    <td className="px-5 py-3">{u.area_sqft} sq.ft</td>
                    <td className="px-5 py-3 font-semibold">{inr(u.price)}</td>
                    <td className="px-5 py-3">
                      <Select value={u.status} onValueChange={(v) => updateUnitStatus(u.id, v)}>
                        <SelectTrigger className="h-8 w-32" data-testid={`unit-status-${u.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["available", "booked", "sold"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {units.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No units yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="buyers" className="mt-6" data-testid="builder-buyers-panel">
          <div className="mb-4"><Button data-testid="add-buyer-button" onClick={() => setShowBuyer(true)}><Plus className="mr-1.5 h-4 w-4" /> Add Buyer</Button></div>
          <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500">
                <tr><th className="px-5 py-3">Buyer</th><th className="px-5 py-3">Contact</th><th className="px-5 py-3">Project</th><th className="px-5 py-3">KYC</th><th className="px-5 py-3">Booking</th></tr>
              </thead>
              <tbody>
                {buyers.map((b) => (
                  <tr key={b.id} className="border-t border-slate-100" data-testid={`buyer-row-${b.id}`}>
                    <td className="px-5 py-3 font-semibold">{b.name}</td>
                    <td className="px-5 py-3 text-xs text-slate-500">{b.phone}<br />{b.email}</td>
                    <td className="px-5 py-3">{b.project_name || "—"}</td>
                    <td className="px-5 py-3">
                      <Select value={b.kyc_status} onValueChange={(v) => updateBuyer(b.id, { kyc_status: v })}>
                        <SelectTrigger className="h-8 w-32" data-testid={`buyer-kyc-${b.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["Pending", "Documents Received", "Verified"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                    <td className="px-5 py-3">
                      <Select value={b.booking_status} onValueChange={(v) => updateBuyer(b.id, { booking_status: v })}>
                        <SelectTrigger className="h-8 w-36" data-testid={`buyer-booking-${b.id}`}><SelectValue /></SelectTrigger>
                        <SelectContent>{["Enquiry", "Booked", "Agreement", "Registered"].map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                      </Select>
                    </td>
                  </tr>
                ))}
                {buyers.length === 0 && <tr><td colSpan={5} className="px-5 py-8 text-center text-slate-500">No buyers yet.</td></tr>}
              </tbody>
            </table>
          </div>
        </TabsContent>

        <TabsContent value="applications" className="mt-6" data-testid="builder-applications-panel">
          <div className="space-y-3">
            {applications.map((a) => (
              <div key={a.id} className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5" data-testid={`application-row-${a.id}`}>
                <div>
                  <p className="text-sm font-semibold text-slate-900">{a.applicant_name} <span className="font-normal text-slate-500">({a.applicant_email})</span></p>
                  <p className="mt-1 text-xs text-slate-500">
                    {a.project_name} · Unit {a.unit?.unit_no} · {a.unit?.unit_type} · {inr(a.unit?.price)}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`rounded-full px-2.5 py-1 text-[11px] font-bold uppercase ${
                    a.status === "approved" ? "bg-emerald-50 text-emerald-700" : a.status === "rejected" ? "bg-red-50 text-red-600" : "bg-amber-50 text-amber-700"}`}
                    data-testid={`application-status-${a.id}`}>{a.status.replace("_", " ")}</span>
                  {a.status === "submitted" && (
                    <>
                      <Button size="sm" variant="outline" data-testid={`application-approve-${a.id}`} onClick={() => updateApplication(a.id, "approved")}>Approve</Button>
                      <Button size="sm" variant="ghost" className="text-red-600" data-testid={`application-reject-${a.id}`} onClick={() => updateApplication(a.id, "rejected")}>Reject</Button>
                    </>
                  )}
                </div>
              </div>
            ))}
            {applications.length === 0 && <p className="text-sm text-slate-500">No applications received yet.</p>}
          </div>
        </TabsContent>

        <TabsContent value="verifications" className="mt-6" data-testid="builder-verifications-panel">
          <div className="space-y-3">
            {verifications.map((v) => (
              <button key={v.id} onClick={() => navigate(`/verification/${v.id}`)} data-testid={`builder-verification-row-${v.id}`}
                className="flex w-full flex-wrap items-center justify-between gap-3 rounded-xl border border-slate-200 bg-white p-5 text-left transition-shadow hover:shadow-md">
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-slate-900">{v.property.address}</p>
                  <p className="mt-1 text-xs text-slate-500">Survey: {v.property.survey_number || "—"}{v.risk ? ` · Risk: ${v.risk.level}` : ""}</p>
                </div>
                <div className="flex items-center gap-3">
                  <StatusPill status={v.status} label={v.status_label} />
                  <ArrowRight className="h-4 w-4 text-slate-400" />
                </div>
              </button>
            ))}
            {verifications.length === 0 && <p className="text-sm text-slate-500">No verification requests yet. Verify title documents for any project property.</p>}
          </div>
        </TabsContent>
      </Tabs>
    </PortalShell>
  );
}
